import React, { useRef, useState } from 'react';

import { MoreVertRounded } from '@mui/icons-material';
import {
  IconButton, Menu, useMediaQuery, useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import Button from '../Button';
import DataGrid, { DataGridProps } from '../DataGrid';
import SearchField from '../SearchField';

import {
  ControlContainer, GrowContainer, UsersListContainer, UsersSearch,
} from './index.styles';

interface UsersListProps extends Omit<DataGridProps, 'rows' | 'columns'> {
  columns: GridColDef[],
  menuItems?: React.ReactElement | React.ReactElement[],
  data: any[],
  loading: boolean,
  onAddClick?: () => void,
  onSearch?: (searchStr: string) => void,
  addButtonLabel?: string,
  selectedRowId?: string,
}

const UsersList = ({ columns,
  data,
  menuItems,
  loading,
  onAddClick,
  onSearch,
  selectedRowId,
  addButtonLabel = 'Add User',
  ...props }: UsersListProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const menuRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <UsersListContainer>
      {onSearch && (
        <ControlContainer>
          <UsersSearch>
            <SearchField
              collapseEnabled
              placeholder={isMd ? 'Filter Users...' : 'Filter...'}
              onSearch={(searchStr) => onSearch(searchStr)}
            />
          </UsersSearch>
          {onAddClick && (
            <Button
              size="medium"
              onClick={onAddClick}
            >
              {isMd ? `+ ${addButtonLabel}` : '+'}
            </Button>
          )}
          {menuItems && (
            <IconButton
              ref={menuRef}
              onClick={() => setMenuOpen(true)}
            >
              <MoreVertRounded />
            </IconButton>
          )}
        </ControlContainer>
      )}
      <GrowContainer>
        <DataGrid
          rows={data}
          columns={columns}
          pageSizeOptions={[10, 30, 50, 100]}
          getRowId={(row) => row.id}
          autoHeight
          loading={loading && !!data}
          selectedRowId={selectedRowId}
          {...props}
        />
      </GrowContainer>
      <Menu
        anchorEl={menuRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onClick={() => setMenuOpen(false)}
        disableScrollLock
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {menuItems}
      </Menu>
    </UsersListContainer>
  );
};

export default UsersList;
